// Plans the client has linked that have not started yet (DEV-315): between
// linking and the plan-year start the header showed "0 active plans" and the
// Benefits Overview said "No plans configured" while two plans were set up to
// start Nov 1. Both surfaces read this one summary so they tell the same story.
import { format } from "date-fns";
import { parseDateLocal } from "@/lib/utils";

export interface UpcomingPlanLike {
  effectiveDate?: string | Date | null;
  endDate?: string | Date | null;
  status?: string | null;
  isActive?: boolean | null;
}

export interface UpcomingPlansSummary {
  /** Linked plans whose plan year starts after `today`. */
  count: number;
  /** The earliest of those start dates (local calendar date), if any. */
  firstStart: Date | null;
}

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** True when the plan's year starts after `today` (compared as calendar dates,
 * so a "2026-11-01T07:00:00Z" link start is Nov 1, never Oct 31). */
export function isUpcomingPlan(plan: UpcomingPlanLike, today: Date = new Date()): boolean {
  if (plan.isActive === false) return false;
  const status = String(plan.status ?? "active").toLowerCase();
  if (status !== "active" && status !== "ready" && status !== "future") return false;
  const start = plan.effectiveDate ? parseDateLocal(plan.effectiveDate) : null;
  if (!start) return false;
  return start.getTime() > startOfDay(today).getTime();
}

export function upcomingPlansSummary(plans: UpcomingPlanLike[] | null | undefined, today: Date = new Date()): UpcomingPlansSummary {
  let count = 0;
  let firstStart: Date | null = null;
  for (const plan of plans || []) {
    if (!isUpcomingPlan(plan, today)) continue;
    count += 1;
    const start = parseDateLocal(plan.effectiveDate)!;
    if (!firstStart || start.getTime() < firstStart.getTime()) firstStart = start;
  }
  return { count, firstStart };
}

/** "2 plans start Nov 1, 2026" / "1 plan starts Nov 1, 2026"; null when nothing is upcoming. */
export function upcomingPlansMessage(plans: UpcomingPlanLike[] | null | undefined, today: Date = new Date()): string | null {
  const { count, firstStart } = upcomingPlansSummary(plans, today);
  if (count === 0 || !firstStart) return null;
  const noun = count === 1 ? "plan starts" : "plans start";
  return `${count} ${noun} ${format(firstStart, "MMM d, yyyy")}`;
}
